/**
 * Furniture pixel data — desks, chairs, monitors, plants, etc.
 *
 * Uses FURNITURE_PALETTE from palette.ts:
 *   0 = transparent
 *   1 = desk surface
 *   2 = desk shadow
 *   3 = desk highlight
 *   4 = outline
 *   5 = metal
 *   6 = chair grey
 *   7 = white
 *   8 = plant green
 *   9 = plant dark
 *  10 = pot brown
 *  11 = pot shadow
 *  12 = dark surface
 *  13 = light surface
 */

import type { SpriteDefinition } from "../SpriteSheet.js";

// Helpers
function px(pixels: number[], w: number, x: number, y: number, color: number): void {
  if (x >= 0 && x < w && y >= 0 && y < (pixels.length / w)) {
    pixels[y * w + x] = color;
  }
}

function rect(pixels: number[], w: number, x: number, y: number, rw: number, rh: number, color: number): void {
  for (let dy = 0; dy < rh; dy++) {
    for (let dx = 0; dx < rw; dx++) {
      px(pixels, w, x + dx, y + dy, color);
    }
  }
}

// ==================================================================
// LARGE DESK — for managers/entry points (48x28)
// ==================================================================

function makeLargeDesk(): number[] {
  const W = 48, H = 28;
  const pixels = new Array<number>(W * H).fill(0);

  // Desk top surface (isometric slab feel)
  rect(pixels, W, 2, 6, 44, 6, 1);   // main surface
  rect(pixels, W, 2, 6, 44, 1, 3);   // top highlight
  rect(pixels, W, 2, 12, 44, 1, 2);  // bottom shadow

  // Desk front panel
  rect(pixels, W, 2, 12, 44, 14, 2);
  rect(pixels, W, 4, 14, 40, 10, 1);
  // Panel detail — lighter inset
  rect(pixels, W, 8, 16, 14, 6, 3);
  rect(pixels, W, 26, 16, 14, 6, 3);

  // Outline
  for (let x = 1; x < 47; x++) { px(pixels, W, x, 5, 4); px(pixels, W, x, 26, 4); }
  for (let y = 5; y < 27; y++) { px(pixels, W, 1, y, 4); px(pixels, W, 46, y, 4); }

  // Desk legs (barely visible at bottom)
  rect(pixels, W, 4, 25, 2, 3, 5);
  rect(pixels, W, 42, 25, 2, 3, 5);

  return pixels;
}

export const FURNITURE_LARGE_DESK: SpriteDefinition = {
  name: "desk-large",
  frames: [{ width: 48, height: 28, pixels: makeLargeDesk() }],
  originX: 24,
  originY: 12, // anchor at desk surface level
};

// ==================================================================
// SMALL DESK — for regular workers (40x24)
// ==================================================================

function makeSmallDesk(): number[] {
  const W = 40, H = 24;
  const pixels = new Array<number>(W * H).fill(0);

  // Desk top
  rect(pixels, W, 2, 4, 36, 5, 1);
  rect(pixels, W, 2, 4, 36, 1, 3);
  rect(pixels, W, 2, 9, 36, 1, 2);

  // Desk front
  rect(pixels, W, 2, 9, 36, 12, 2);
  rect(pixels, W, 4, 11, 32, 8, 1);
  // Panel inset
  rect(pixels, W, 8, 13, 24, 4, 3);

  // Outline
  for (let x = 1; x < 39; x++) { px(pixels, W, x, 3, 4); px(pixels, W, x, 22, 4); }
  for (let y = 3; y < 23; y++) { px(pixels, W, 1, y, 4); px(pixels, W, 38, y, 4); }

  // Legs
  rect(pixels, W, 3, 20, 2, 4, 5);
  rect(pixels, W, 35, 20, 2, 4, 5);

  return pixels;
}

export const FURNITURE_SMALL_DESK: SpriteDefinition = {
  name: "desk-small",
  frames: [{ width: 40, height: 24, pixels: makeSmallDesk() }],
  originX: 20,
  originY: 9,
};

// ==================================================================
// POTTED PLANT (16x24)
// ==================================================================

function makePlant(): number[] {
  const W = 16, H = 24;
  const pixels = new Array<number>(W * H).fill(0);

  // Pot
  rect(pixels, W, 4, 16, 8, 8, 10);
  rect(pixels, W, 5, 16, 6, 1, 11); // rim
  rect(pixels, W, 5, 17, 6, 1, 10);
  // Pot shadow
  rect(pixels, W, 6, 22, 4, 2, 11);

  // Leaves (organic blobs)
  rect(pixels, W, 4, 8, 8, 8, 8);
  rect(pixels, W, 2, 10, 4, 4, 8);
  rect(pixels, W, 10, 10, 4, 4, 8);
  rect(pixels, W, 5, 6, 6, 3, 8);
  // Darker leaf centers
  rect(pixels, W, 6, 10, 4, 3, 9);
  rect(pixels, W, 5, 7, 2, 2, 9);
  // Stem
  rect(pixels, W, 7, 14, 2, 3, 9);

  return pixels;
}

export const FURNITURE_PLANT: SpriteDefinition = {
  name: "plant",
  frames: [{ width: 16, height: 24, pixels: makePlant() }],
  originX: 8,
  originY: 20,
};

// ==================================================================
// WATER COOLER (14x28)
// ==================================================================

function makeWaterCooler(): number[] {
  const W = 14, H = 28;
  const pixels = new Array<number>(W * H).fill(0);

  // Water jug (top)
  rect(pixels, W, 4, 0, 6, 8, 7);   // water bottle (white/clear)
  rect(pixels, W, 5, 2, 4, 5, 13);  // water inside (lighter)
  // Outline
  for (let x = 4; x < 10; x++) { px(pixels, W, x, 0, 4); }
  for (let y = 0; y < 8; y++) { px(pixels, W, 3, y, 4); px(pixels, W, 10, y, 4); }

  // Dispenser body
  rect(pixels, W, 3, 8, 8, 14, 6);
  rect(pixels, W, 4, 9, 6, 12, 13);
  // Tap
  px(pixels, W, 5, 14, 5);
  px(pixels, W, 5, 15, 5);

  // Legs
  rect(pixels, W, 3, 22, 2, 6, 5);
  rect(pixels, W, 9, 22, 2, 6, 5);
  // Base
  rect(pixels, W, 2, 26, 10, 2, 12);

  return pixels;
}

export const FURNITURE_WATER_COOLER: SpriteDefinition = {
  name: "water-cooler",
  frames: [{ width: 14, height: 28, pixels: makeWaterCooler() }],
  originX: 7,
  originY: 24,
};

// ==================================================================
// CONVEYOR SEGMENT — for pipeline scenes (48x12)
// ==================================================================

function makeConveyorSegment(): number[] {
  const W = 48, H = 12;
  const pixels = new Array<number>(W * H).fill(0);

  // Belt surface
  rect(pixels, W, 0, 2, 48, 6, 5);  // metal belt
  rect(pixels, W, 0, 2, 48, 1, 13); // highlight top
  rect(pixels, W, 0, 8, 48, 1, 12); // shadow bottom

  // Belt segments (visual markers)
  for (let x = 4; x < 48; x += 8) {
    rect(pixels, W, x, 3, 1, 5, 12);
  }

  // Support legs
  rect(pixels, W, 2, 8, 3, 4, 6);
  rect(pixels, W, 22, 8, 3, 4, 6);
  rect(pixels, W, 43, 8, 3, 4, 6);

  return pixels;
}

export const FURNITURE_CONVEYOR: SpriteDefinition = {
  name: "conveyor",
  frames: [{ width: 48, height: 12, pixels: makeConveyorSegment() }],
  originX: 24,
  originY: 6,
};

// ==================================================================
// JUDGE BENCH — for debate scenes (56x32)
// ==================================================================

function makeJudgeBench(): number[] {
  const W = 56, H = 32;
  const pixels = new Array<number>(W * H).fill(0);

  // Elevated desk surface
  rect(pixels, W, 2, 8, 52, 6, 1);
  rect(pixels, W, 2, 8, 52, 1, 3);
  rect(pixels, W, 2, 14, 52, 1, 2);

  // Front panel (taller than normal desk)
  rect(pixels, W, 2, 14, 52, 16, 2);
  rect(pixels, W, 4, 16, 48, 12, 1);

  // Decorative panel insets
  rect(pixels, W, 8, 18, 16, 8, 3);
  rect(pixels, W, 32, 18, 16, 8, 3);

  // Central emblem (simple diamond shape)
  px(pixels, W, 27, 19, 7);
  rect(pixels, W, 26, 20, 3, 1, 7);
  rect(pixels, W, 25, 21, 5, 1, 7);
  rect(pixels, W, 26, 22, 3, 1, 7);
  px(pixels, W, 27, 23, 7);

  // Outline
  for (let x = 1; x < 55; x++) { px(pixels, W, x, 7, 4); px(pixels, W, x, 30, 4); }
  for (let y = 7; y <= 30; y++) { px(pixels, W, 1, y, 4); px(pixels, W, 54, y, 4); }

  return pixels;
}

export const FURNITURE_JUDGE_BENCH: SpriteDefinition = {
  name: "judge-bench",
  frames: [{ width: 56, height: 32, pixels: makeJudgeBench() }],
  originX: 28,
  originY: 14,
};

// ==================================================================
// PODIUM — for debate scenes (24x28)
// ==================================================================

function makePodium(): number[] {
  const W = 24, H = 28;
  const pixels = new Array<number>(W * H).fill(0);

  // Podium top surface
  rect(pixels, W, 4, 4, 16, 4, 1);
  rect(pixels, W, 4, 4, 16, 1, 3);
  rect(pixels, W, 4, 8, 16, 1, 2);

  // Front body (tapers slightly)
  rect(pixels, W, 5, 8, 14, 16, 2);
  rect(pixels, W, 6, 10, 12, 12, 1);

  // Detail strip
  rect(pixels, W, 8, 14, 8, 2, 3);

  // Base
  rect(pixels, W, 3, 24, 18, 3, 2);
  rect(pixels, W, 3, 24, 18, 1, 1);

  // Outline
  for (let x = 3; x < 21; x++) { px(pixels, W, x, 3, 4); px(pixels, W, x, 27, 4); }
  for (let y = 3; y < 28; y++) { px(pixels, W, 3, y, 4); px(pixels, W, 20, y, 4); }

  return pixels;
}

export const FURNITURE_PODIUM: SpriteDefinition = {
  name: "podium",
  frames: [{ width: 24, height: 28, pixels: makePodium() }],
  originX: 12,
  originY: 14,
};

// ==================================================================
// BOOKSHELF — for hierarchical scenes (32x32)
// ==================================================================

function makeBookshelf(): number[] {
  const W = 32, H = 32;
  const pixels = new Array<number>(W * H).fill(0);

  // Frame
  rect(pixels, W, 2, 2, 28, 28, 1);
  // Outline
  for (let x = 1; x < 31; x++) { px(pixels, W, x, 1, 4); px(pixels, W, x, 30, 4); }
  for (let y = 1; y <= 30; y++) { px(pixels, W, 1, y, 4); px(pixels, W, 30, y, 4); }

  // Shelves (3 rows)
  rect(pixels, W, 3, 10, 26, 2, 3);
  rect(pixels, W, 3, 19, 26, 2, 3);
  rect(pixels, W, 3, 28, 26, 2, 3);

  // Books (colored rectangles in each shelf section)
  // Top shelf
  rect(pixels, W, 4, 3, 3, 7, 12); // dark book
  rect(pixels, W, 8, 4, 3, 6, 13); // light book
  rect(pixels, W, 12, 3, 2, 7, 5); // metal/grey book
  rect(pixels, W, 16, 5, 4, 5, 12);
  rect(pixels, W, 22, 3, 3, 7, 13);
  rect(pixels, W, 26, 4, 3, 6, 5);

  // Middle shelf
  rect(pixels, W, 4, 12, 4, 7, 13);
  rect(pixels, W, 9, 13, 3, 6, 12);
  rect(pixels, W, 14, 12, 2, 7, 5);
  rect(pixels, W, 18, 13, 5, 6, 13);
  rect(pixels, W, 24, 12, 4, 7, 12);

  // Bottom shelf
  rect(pixels, W, 5, 21, 3, 7, 5);
  rect(pixels, W, 10, 22, 4, 6, 12);
  rect(pixels, W, 16, 21, 3, 7, 13);
  rect(pixels, W, 21, 22, 2, 6, 5);
  rect(pixels, W, 25, 21, 3, 7, 12);

  return pixels;
}

export const FURNITURE_BOOKSHELF: SpriteDefinition = {
  name: "bookshelf",
  frames: [{ width: 32, height: 32, pixels: makeBookshelf() }],
  originX: 16,
  originY: 28,
};

// ==================================================================
// FILING CABINET (16x24) — two drawers with handles
// ==================================================================

function makeFilingCabinet(): number[] {
  const W = 16, H = 24;
  const pixels = new Array<number>(W * H).fill(0);

  // Body
  rect(pixels, W, 2, 2, 12, 20, 5); // metal grey body
  rect(pixels, W, 3, 3, 10, 18, 6); // slightly lighter interior

  // Top drawer
  rect(pixels, W, 3, 3, 10, 8, 5);
  rect(pixels, W, 4, 4, 8, 6, 6);
  // Handle
  rect(pixels, W, 6, 6, 4, 2, 7);

  // Divider
  rect(pixels, W, 3, 11, 10, 1, 4);

  // Bottom drawer
  rect(pixels, W, 3, 12, 10, 8, 5);
  rect(pixels, W, 4, 13, 8, 6, 6);
  // Handle
  rect(pixels, W, 6, 15, 4, 2, 7);

  // Outline
  for (let x = 1; x < 15; x++) { px(pixels, W, x, 1, 4); px(pixels, W, x, 22, 4); }
  for (let y = 1; y < 23; y++) { px(pixels, W, 1, y, 4); px(pixels, W, 14, y, 4); }

  // Feet
  rect(pixels, W, 3, 22, 2, 2, 4);
  rect(pixels, W, 11, 22, 2, 2, 4);

  return pixels;
}

export const FURNITURE_FILING_CABINET: SpriteDefinition = {
  name: "filing-cabinet",
  frames: [{ width: 16, height: 24, pixels: makeFilingCabinet() }],
  originX: 8,
  originY: 20,
};

// ==================================================================
// WHITEBOARD (32x28) — white rectangle on stand with colored scribbles
// ==================================================================

function makeWhiteboard(): number[] {
  const W = 32, H = 28;
  const pixels = new Array<number>(W * H).fill(0);

  // Stand legs
  rect(pixels, W, 6, 20, 2, 8, 5);
  rect(pixels, W, 24, 20, 2, 8, 5);
  // Cross bar
  rect(pixels, W, 6, 24, 20, 1, 5);

  // Board frame
  rect(pixels, W, 2, 2, 28, 18, 6);
  // White surface
  rect(pixels, W, 3, 3, 26, 16, 7);

  // Scribble lines (colored marks to show it's been used)
  rect(pixels, W, 5, 5, 12, 1, 12); // dark line
  rect(pixels, W, 5, 7, 18, 1, 12);
  rect(pixels, W, 5, 9, 8, 1, 13);  // light line
  rect(pixels, W, 16, 9, 8, 1, 12);
  rect(pixels, W, 5, 11, 14, 1, 12);
  rect(pixels, W, 5, 13, 10, 1, 13);
  rect(pixels, W, 18, 13, 6, 1, 12);

  // Outline
  for (let x = 1; x < 31; x++) { px(pixels, W, x, 1, 4); px(pixels, W, x, 20, 4); }
  for (let y = 1; y < 21; y++) { px(pixels, W, 1, y, 4); px(pixels, W, 30, y, 4); }

  return pixels;
}

export const FURNITURE_WHITEBOARD: SpriteDefinition = {
  name: "whiteboard",
  frames: [{ width: 32, height: 28, pixels: makeWhiteboard() }],
  originX: 16,
  originY: 24,
};

// ==================================================================
// RUG (48x24) — flat isometric oval carpet
// ==================================================================

function makeRug(): number[] {
  const W = 48, H = 24;
  const pixels = new Array<number>(W * H).fill(0);

  // Draw a flattened isometric diamond shape (rug is flat on the floor)
  for (let row = 0; row < H; row++) {
    let halfWidth: number;
    if (row < H / 2) {
      halfWidth = Math.floor((row + 1) * (W / 2) / (H / 2));
    } else {
      halfWidth = Math.floor((H - row) * (W / 2) / (H / 2));
    }

    const centerX = W / 2;
    const startX = Math.max(0, centerX - halfWidth);
    const endX = Math.min(W, centerX + halfWidth);

    for (let x = startX; x < endX; x++) {
      const off = row * W + x;
      // Border
      if (x <= startX + 1 || x >= endX - 2 || row <= 1 || row >= H - 2) {
        pixels[off] = 12; // dark border
      } else {
        pixels[off] = 13; // light interior
      }
    }
  }

  return pixels;
}

export const FURNITURE_RUG: SpriteDefinition = {
  name: "rug",
  frames: [{ width: 48, height: 24, pixels: makeRug() }],
  originX: 24,
  originY: 12,
};

// ==================================================================
// COFFEE MUG (8x10) — tiny mug, great for compositional detail
// ==================================================================

function makeCoffeeMug(): number[] {
  const W = 8, H = 10;
  const pixels = new Array<number>(W * H).fill(0);

  // Mug body
  rect(pixels, W, 1, 2, 5, 7, 7); // white mug
  rect(pixels, W, 2, 3, 3, 5, 13); // slightly darker interior

  // Handle
  px(pixels, W, 6, 4, 7);
  px(pixels, W, 7, 5, 7);
  px(pixels, W, 7, 6, 7);
  px(pixels, W, 6, 7, 7);

  // Coffee (dark inside top)
  rect(pixels, W, 2, 3, 3, 2, 12);

  // Rim
  rect(pixels, W, 1, 2, 5, 1, 5);

  // Outline
  for (let x = 0; x < 6; x++) { px(pixels, W, x, 1, 4); px(pixels, W, x, 9, 4); }
  for (let y = 1; y < 10; y++) { px(pixels, W, 0, y, 4); px(pixels, W, 5, y, 4); }

  return pixels;
}

export const FURNITURE_COFFEE_MUG: SpriteDefinition = {
  name: "coffee-mug",
  frames: [{ width: 8, height: 10, pixels: makeCoffeeMug() }],
  originX: 4,
  originY: 8,
};

// ==================================================================
// PAPER STACK (12x8) — small pile of papers
// ==================================================================

function makePaperStack(): number[] {
  const W = 12, H = 8;
  const pixels = new Array<number>(W * H).fill(0);

  // Bottom paper (slightly offset)
  rect(pixels, W, 2, 4, 9, 4, 7);
  rect(pixels, W, 2, 4, 9, 1, 5);

  // Middle paper
  rect(pixels, W, 1, 2, 9, 4, 7);
  rect(pixels, W, 1, 2, 9, 1, 5);

  // Top paper
  rect(pixels, W, 0, 0, 9, 4, 7);
  rect(pixels, W, 0, 0, 9, 1, 5);

  // Text lines on top paper
  rect(pixels, W, 1, 1, 5, 1, 12);
  rect(pixels, W, 1, 3, 6, 1, 12);

  return pixels;
}

export const FURNITURE_PAPER_STACK: SpriteDefinition = {
  name: "paper-stack",
  frames: [{ width: 12, height: 8, pixels: makePaperStack() }],
  originX: 6,
  originY: 6,
};

// ==================================================================
// MONITOR BANK (56x20) — triple-monitor setup for manager desks
// ==================================================================

function makeMonitorBank(): number[] {
  const W = 56, H = 20;
  const pixels = new Array<number>(W * H).fill(0);

  // Three monitors side by side with small gaps
  for (let m = 0; m < 3; m++) {
    const mx = m * 18 + 1;

    // Monitor bezel
    rect(pixels, W, mx, 0, 16, 14, 4);
    rect(pixels, W, mx + 1, 1, 14, 12, 6);

    // Screen (dark with glow)
    rect(pixels, W, mx + 2, 2, 12, 10, 12);

    // Screen content (subtle lines)
    rect(pixels, W, mx + 3, 3, 8, 1, 13);
    rect(pixels, W, mx + 3, 5, 10, 1, 13);
    rect(pixels, W, mx + 3, 7, 6, 1, 13);
    rect(pixels, W, mx + 3, 9, 9, 1, 13);

    // Stand
    rect(pixels, W, mx + 6, 14, 4, 4, 5);

    // Base
    rect(pixels, W, mx + 3, 18, 10, 2, 5);
  }

  return pixels;
}

export const FURNITURE_MONITOR_BANK: SpriteDefinition = {
  name: "monitor-bank",
  frames: [{ width: 56, height: 20, pixels: makeMonitorBank() }],
  originX: 28,
  originY: 16,
};

// ==================================================================
// SERVER RACK (20x28) — tall metal rack with LED indicators
// ==================================================================

function makeServerRack(): number[] {
  const W = 20, H = 28;
  const pixels = new Array<number>(W * H).fill(0);

  // Main body
  rect(pixels, W, 2, 2, 16, 24, 5); // metal body
  rect(pixels, W, 3, 3, 14, 22, 6); // inner panel

  // Rack rails (sides)
  rect(pixels, W, 3, 3, 1, 22, 5);
  rect(pixels, W, 16, 3, 1, 22, 5);

  // Server modules (4 horizontal slots)
  for (let i = 0; i < 4; i++) {
    const y = 4 + i * 5;
    rect(pixels, W, 4, y, 12, 4, 12); // dark module face
    rect(pixels, W, 4, y, 12, 1, 5);  // top edge highlight
    // LED indicators (green dots)
    px(pixels, W, 14, y + 1, 8);
    px(pixels, W, 14, y + 2, 8);
    // Vent lines
    rect(pixels, W, 5, y + 2, 6, 1, 6);
  }

  // Outline
  for (let x = 1; x < 19; x++) { px(pixels, W, x, 1, 4); px(pixels, W, x, 26, 4); }
  for (let y = 1; y < 27; y++) { px(pixels, W, 1, y, 4); px(pixels, W, 18, y, 4); }

  // Feet
  rect(pixels, W, 3, 26, 2, 2, 4);
  rect(pixels, W, 15, 26, 2, 2, 4);

  return pixels;
}

export const FURNITURE_SERVER_RACK: SpriteDefinition = {
  name: "server-rack",
  frames: [{ width: 20, height: 28, pixels: makeServerRack() }],
  originX: 10,
  originY: 24,
};

// ==================================================================
// COMMS STATION (16x20) — radio/antenna device for messaging
// ==================================================================

function makeCommsStation(): number[] {
  const W = 16, H = 20;
  const pixels = new Array<number>(W * H).fill(0);

  // Antenna
  px(pixels, W, 8, 0, 5);
  px(pixels, W, 8, 1, 5);
  px(pixels, W, 8, 2, 5);
  px(pixels, W, 8, 3, 5);
  // Antenna tip
  px(pixels, W, 7, 0, 8);
  px(pixels, W, 9, 0, 8);

  // Base unit body
  rect(pixels, W, 2, 4, 12, 14, 5);  // metal body
  rect(pixels, W, 3, 5, 10, 12, 6);  // inner face

  // Small screen
  rect(pixels, W, 4, 6, 8, 5, 12);   // dark screen
  rect(pixels, W, 5, 7, 6, 3, 13);   // screen content (lighter)
  // Signal bars on screen
  px(pixels, W, 6, 9, 8);
  rect(pixels, W, 8, 8, 1, 2, 8);
  rect(pixels, W, 10, 7, 1, 3, 8);

  // Control buttons
  rect(pixels, W, 4, 12, 2, 2, 7);   // white button
  rect(pixels, W, 7, 12, 2, 2, 12);  // dark button
  rect(pixels, W, 10, 12, 2, 2, 8);  // green button

  // Speaker grille
  for (let y = 15; y < 17; y++) {
    for (let x = 4; x < 12; x += 2) {
      px(pixels, W, x, y, 12);
    }
  }

  // Outline
  for (let x = 1; x < 15; x++) { px(pixels, W, x, 3, 4); px(pixels, W, x, 18, 4); }
  for (let y = 3; y < 19; y++) { px(pixels, W, 1, y, 4); px(pixels, W, 14, y, 4); }

  return pixels;
}

export const FURNITURE_COMMS_STATION: SpriteDefinition = {
  name: "comms-station",
  frames: [{ width: 16, height: 20, pixels: makeCommsStation() }],
  originX: 8,
  originY: 16,
};

// ==================================================================
// INBOX TRAY (16x14) — stacked paper trays for task queue
// ==================================================================

function makeInboxTray(): number[] {
  const W = 16, H = 14;
  const pixels = new Array<number>(W * H).fill(0);

  // Bottom tray
  rect(pixels, W, 1, 9, 14, 4, 5);   // metal tray
  rect(pixels, W, 2, 10, 12, 2, 6);  // tray interior
  // Papers in bottom tray
  rect(pixels, W, 3, 10, 10, 2, 7);
  rect(pixels, W, 4, 10, 6, 1, 12);  // text line

  // Top tray
  rect(pixels, W, 1, 4, 14, 4, 5);   // metal tray
  rect(pixels, W, 2, 5, 12, 2, 6);   // tray interior
  // Papers in top tray
  rect(pixels, W, 3, 5, 10, 2, 7);
  rect(pixels, W, 4, 5, 8, 1, 12);   // text line

  // Tray supports (vertical posts)
  rect(pixels, W, 2, 0, 1, 14, 5);
  rect(pixels, W, 13, 0, 1, 14, 5);

  // Labels
  px(pixels, W, 2, 0, 8);  // green dot label (inbox)
  px(pixels, W, 13, 0, 8);

  // Outline edges
  for (let x = 0; x < 16; x++) { px(pixels, W, x, 13, 4); }
  px(pixels, W, 0, 9, 4); px(pixels, W, 15, 9, 4);
  px(pixels, W, 0, 4, 4); px(pixels, W, 15, 4, 4);

  return pixels;
}

export const FURNITURE_INBOX_TRAY: SpriteDefinition = {
  name: "inbox-tray",
  frames: [{ width: 16, height: 14, pixels: makeInboxTray() }],
  originX: 8,
  originY: 11,
};

// ==================================================================
// BULLETIN BOARD (28x24) — cork board with pinned notes
// ==================================================================

function makeBulletinBoard(): number[] {
  const W = 28, H = 24;
  const pixels = new Array<number>(W * H).fill(0);

  // Frame
  rect(pixels, W, 1, 1, 26, 20, 4);  // dark frame
  // Cork surface
  rect(pixels, W, 2, 2, 24, 18, 10); // pot brown = cork color
  // Cork texture variation
  for (let y = 3; y < 19; y += 2) {
    for (let x = 3; x < 25; x += 3) {
      px(pixels, W, x, y, 11); // darker cork spots
    }
  }

  // Pinned notes (small rectangles with pin dots)
  // Note 1 — top left
  rect(pixels, W, 4, 4, 6, 5, 7);   // white note
  rect(pixels, W, 5, 5, 4, 1, 12);  // text line
  rect(pixels, W, 5, 7, 3, 1, 12);  // text line
  px(pixels, W, 6, 3, 8);           // green pin

  // Note 2 — top right
  rect(pixels, W, 16, 3, 7, 6, 13); // grey note
  rect(pixels, W, 17, 4, 5, 1, 12); // text line
  rect(pixels, W, 17, 6, 4, 1, 12); // text line
  px(pixels, W, 19, 2, 8);          // green pin

  // Note 3 — bottom center
  rect(pixels, W, 9, 12, 8, 5, 7);  // white note
  rect(pixels, W, 10, 13, 6, 1, 12); // text line
  rect(pixels, W, 10, 15, 5, 1, 12); // text line
  px(pixels, W, 12, 11, 8);          // green pin

  // Note 4 — bottom left
  rect(pixels, W, 3, 13, 5, 4, 13); // grey note
  rect(pixels, W, 4, 14, 3, 1, 12); // text line
  px(pixels, W, 5, 12, 8);          // green pin

  // Stand legs
  rect(pixels, W, 6, 20, 2, 4, 5);
  rect(pixels, W, 20, 20, 2, 4, 5);

  return pixels;
}

export const FURNITURE_BULLETIN_BOARD: SpriteDefinition = {
  name: "bulletin-board",
  frames: [{ width: 28, height: 24, pixels: makeBulletinBoard() }],
  originX: 14,
  originY: 20,
};
